/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 31/08/2025 - 13:34:16
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 31/08/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  Code as CodeIcon,
  PhoneAndroid as PhoneAndroidIcon,
  PhoneIphone as PhoneIphoneIcon,
  ShoppingCart as ShoppingCartIcon,
  Storage as StorageIcon,
  ColorLens as ColorLensIcon,
  Api as ApiIcon,
  CheckCircleOutline as CheckCircleIcon,
  Description as DescriptionIcon,
  DesignServices as DesignServicesIcon,
  DeveloperMode as DeveloperModeIcon,
  BugReport as BugReportIcon,
  Publish as PublishIcon,
  Update as UpdateIcon,
  People as PeopleIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import LetsTalk from '../../components/letsTalk';
import PageTitle from '../../components/PageTitle';

// Define types for our state and data structures
interface VisibilityState {
  hero: boolean;
  overview: boolean;
  features: boolean;
  process: boolean;
  technologies: boolean;
  cta: boolean;
  testimonials: boolean;
}

interface SectionRefs {
  hero: React.RefObject<HTMLElement>;
  overview: React.RefObject<HTMLElement>;
  features: React.RefObject<HTMLElement>;
  process: React.RefObject<HTMLElement>;
  technologies: React.RefObject<HTMLElement>;
  cta: React.RefObject<HTMLElement>;
  testimonials: React.RefObject<HTMLElement>;
}

interface PortfolioProject {
  id: number;
  title: string;
  description: string;
  image: string;
  tags: string[];
}

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface ProcessStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface Technology {
  name: string;
  icon: React.ReactNode;
  delay: number;
}

// Custom styled components
const HeroBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#044D76', // Updated to match CMS/LMS page
  color: theme.palette.common.white,
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(16, 0),
  },
}));

const MobileUIBg = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  overflow: 'hidden',
}));

const GradientCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
}));

const FadeInBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const FeatureCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  backgroundColor: '#ffffff',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
    '& .featureIcon': {
      transform: 'scale(1.1)',
    },
    '& .featureTitle': {
      color: '#044D76', // Updated to match CMS/LMS page
    },
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  transition: 'transform 0.3s ease',
  marginBottom: theme.spacing(2),
}));

const TechCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: '12px',
  textAlign: 'center',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 153, 255, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
    '& .techIcon': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(4, 77, 118, 0.15)',
    }
  },
}));

const ProcessTimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  height: '100%',
  width: '2px',
  backgroundColor: 'rgba(4, 77, 118, 0.2)', // Updated to match CMS/LMS page
  opacity: 0.5,
}));

const ProcessStepBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const ProcessIconBox = styled(Box)(({ theme }) => ({
  zIndex: 10,
  flexShrink: 0,
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#044D76', // Updated to match CMS/LMS page
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[5],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  borderRadius: '12px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[10],
    '& .projectImage': {
      transform: 'scale(1.1)',
    },
    '& .projectOverlay': {
      opacity: 1,
    },
  },
}));

const ProjectImage = styled('img')({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
});

const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(to top, ${theme.palette.common.black}cc, ${theme.palette.common.black}66, transparent)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: theme.spacing(3),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const TechIconBox = styled(Box)(({ theme }) => ({
  height: 64,
  width: 64,
  borderRadius: '50%',
  backgroundColor: 'rgba(4, 77, 118, 0.1)', // Updated to match CMS/LMS page
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  transition: 'all 0.3s ease',
}));

const CodeEditor = styled(Box)(({ theme }) => ({
  backgroundColor: '#282c34',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-5px)',
  },
}));

const EditorHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#21252b',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const EditorDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
}));

const EditorFilename = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.85rem',
  fontFamily: 'monospace',
  marginLeft: 8,
}));

const CodeContent = styled(Box)(({ theme }) => ({
  padding: '16px',
  overflowX: 'auto',
  maxHeight: '500px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const PreformattedCode = styled('pre')(({ theme }) => ({
  margin: 0,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '0.85rem',
  lineHeight: 1.5,
  color: '#e6e6e6',
}));

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.common.white,
  position: 'relative',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 30,
    left: 30,
    width: 60,
    height: 60,
    borderRadius: '50% 0 50% 50%',
    backgroundColor: 'rgba(4, 77, 118, 0.1)',
    zIndex: 0
  }
}));

const TestimonialNavButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#044D76',
  border: '2px solid rgba(4, 77, 118, 0.2)',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(4, 77, 118, 0.1)'
  }
}));

const MobileDevelopmentPage: React.FC = () => {
  const theme = useTheme();
  
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    hero: false,
    overview: false,
    features: false,
    process: false,
    technologies: false,
    cta: false,
    testimonials: false
  });
  
  // Initialize the refs with proper typing
  const sectionRefs: SectionRefs = {
    hero: useRef<HTMLElement>(null),
    overview: useRef<HTMLElement>(null),
    features: useRef<HTMLElement>(null),
    process: useRef<HTMLElement>(null),
    technologies: useRef<HTMLElement>(null),
    cta: useRef<HTMLElement>(null),
    testimonials: useRef<HTMLElement>(null)
  };
  
  // Track when sections come into view
  useEffect(() => {
    // Define type for observers
    const observers: Record<string, IntersectionObserver> = {};
    
    Object.entries(sectionRefs).forEach(([section, ref]) => {
      observers[section] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        },
        { threshold: 0.1 }
      );
      
      if (ref.current) {
        observers[section].observe(ref.current);
      }
    });
    
    return () => {
      Object.entries(observers).forEach(([section, observer]) => {
        if (sectionRefs[section as keyof typeof sectionRefs]?.current) {
          observer.unobserve(sectionRefs[section as keyof typeof sectionRefs].current!);
        }
      });
    };
  }, []);
  
  const handleContactClick = (): void => {
    console.log('Contact us clicked');
    // Add navigation logic here
  };
  
  // Sample portfolio projects
  const portfolioProjects: PortfolioProject[] = [
    {
      id: 1,
      title: 'Fitness Tracker App',
      description: 'A comprehensive fitness tracking application with workout plans, progress monitoring, and social features.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Fitness+App',
      tags: ['React Native', 'Firebase', 'Redux']
    },
    {
      id: 2,
      title: 'Food Delivery Platform',
      description: 'Mobile app for a restaurant chain with order tracking, payment processing, and loyalty rewards.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Food+Delivery',
      tags: ['Flutter', 'Node.js', 'Stripe']
    },
    {
      id: 3,
      title: 'Healthcare Mobile Portal',
      description: 'Patient-focused mobile application for appointment scheduling, medical records access, and telemedicine.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Healthcare+App',
      tags: ['Swift', 'Kotlin', 'AWS']
    }
  ];

  // Feature items
  const featureItems: FeatureItem[] = [
    {
      icon: <PhoneIphoneIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "iOS App Development",
      description: "Native iOS applications built with Swift and SwiftUI that leverage the full potential of Apple's ecosystem for iPhones and iPads.",
      delay: 0
    },
    {
      icon: <PhoneAndroidIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Android App Development",
      description: "Feature-rich Android applications developed with Kotlin and Jetpack Compose for the diverse Android device ecosystem.",
      delay: 100
    },
    {
      icon: <CodeIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Cross-platform Solutions",
      description: "Cost-effective applications built with React Native or Flutter that deliver near-native performance across both iOS and Android platforms.",
      delay: 200
    },
    {
      icon: <StorageIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Mobile App Architecture",
      description: "Robust application architecture that ensures scalability, maintainability, and optimal performance for your mobile solution.",
      delay: 300
    },
    {
      icon: <ColorLensIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "UI/UX Design for Mobile",
      description: "Intuitive, beautiful interface designs optimized for mobile user experiences that follow platform guidelines and best practices.",
      delay: 400
    },
    {
      icon: <ApiIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "API Integration & Backend",
      description: "Seamless integration with APIs and backend services for data synchronization, user authentication, and real-time features.",
      delay: 500
    }
  ];

  // Process steps
  const processSteps: ProcessStep[] = [
    {
      icon: <DescriptionIcon />,
      title: "Requirements Analysis",
      description: "We start by understanding your business goals, target audience, and functional requirements to create a detailed app specification.",
      delay: 0
    },
    {
      icon: <DesignServicesIcon />,
      title: "UI/UX Design",
      description: "Our designers create intuitive user interfaces and engaging experiences tailored specifically for mobile users and platform guidelines.",
      delay: 200
    },
    {
      icon: <DeveloperModeIcon />,
      title: "App Development",
      description: "Our mobile developers build your application using clean architecture principles and the most appropriate technology stack for your needs.",
      delay: 400
    },
    {
      icon: <BugReportIcon />,
      title: "Testing & QA",
      description: "Comprehensive testing across devices, screen sizes, and OS versions to ensure your app works flawlessly in all scenarios.",
      delay: 600
    },
    {
      icon: <PublishIcon />,
      title: "Deployment",
      description: "We handle the submission process to app stores and ensure your application meets all store guidelines for a smooth approval process.",
      delay: 800
    },
    {
      icon: <UpdateIcon />,
      title: "Maintenance & Updates",
      description: "Ongoing support, performance monitoring, and regular updates to keep your app running smoothly and aligned with platform changes.",
      delay: 1000
    }
  ];

  // Technologies
  const technologies: Technology[] = [
    { 
      name: "React Native", 
      icon: <img src="https://d33wubrfki0l68.cloudfront.net/554c3b0e09cf167f0281fda839a5433f2040b349/ecfc9/img/header_logo.svg" alt="React Native" width="48" height="48" />,
      delay: 0 
    },
    { 
      name: "Flutter", 
      icon: <img src="https://storage.googleapis.com/cms-storage-bucket/6e19fee6b47b36ca613f.png" alt="Flutter" width="48" height="48" />,
      delay: 100 
    },
    { 
      name: "Swift", 
      icon: <img src="https://developer.apple.com/swift/images/swift-og.png" alt="Swift" width="48" height="48" />,
      delay: 200 
    },
    { 
      name: "Kotlin", 
      icon: <img src="https://kotlinlang.org/assets/images/open-graph/kotlin_250x250.png" alt="Kotlin" width="48" height="48" />,
      delay: 300 
    },
    { 
      name: "Firebase", 
      icon: <img src="https://firebase.google.com/downloads/brand-guidelines/PNG/logo-logomark.png" alt="Firebase" width="48" height="48" />,
      delay: 400 
    },
    { 
      name: "Redux", 
      icon: <img src="https://redux.js.org/img/redux.svg" alt="Redux" width="48" height="48" />,
      delay: 500 
    },
    { 
      name: "TypeScript", 
      icon: <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Typescript_logo_2020.svg/1200px-Typescript_logo_2020.svg.png" alt="TypeScript" width="48" height="48" />,
      delay: 600 
    },
    { 
      name: "GraphQL", 
      icon: <img src="https://graphql.org/img/logo.svg" alt="GraphQL" width="48" height="48" />,
      delay: 700 
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0099ff0a' }}>
      <PageTitle title="Mobile App Developemnt" />

      <Navbar />
      {/* Hero Section */}
      <HeroBox component="section" ref={sectionRefs.hero}>
        {/* Background elements */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <GradientCircle 
            sx={{ 
              top: 0, 
              right: 0, 
              width: 800, 
              height: 800, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(25%, -25%)'
            }}
          />
          <GradientCircle 
            sx={{ 
              bottom: 0, 
              left: 0, 
              width: 600, 
              height: 600, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(-33%, 33%)'
            }}
          />
          
          {/* Mobile UI patterns */}
          <MobileUIBg>
            <Grid container spacing={4} sx={{ p: 8, height: '100%' }}>
              {[...Array(12)].map((_, i) => (
                <Grid item xs={2} key={i} sx={{ position: 'relative', height: '100%' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 60,
                      height: 120,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'common.white',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.2)' }} />
                    <Box sx={{ p: 1 }}>
                      <Box sx={{ width: '100%', height: 10, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 10, mb: 0.5 }} />
                      <Box sx={{ width: '75%', height: 10, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 10, mb: 1 }} />
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        {[...Array(3)].map((_, j) => (
                          <Box key={j} sx={{ width: 10, height: 10, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%' }} />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </MobileUIBg>
        </Box>

        <Container sx={{ position: 'relative', zIndex: 10 }}>
          <Box maxWidth="md">
            <FadeInBox visible={isVisible.hero}>
              <Typography variant="h2" component="h1" gutterBottom>
                Mobile App
                <Box component="span" display="block" sx={{ color: '#0099ff' }}>Development</Box>
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={200}>
              <Typography variant="h5" paragraph sx={{ color: 'grey.300' }}>
                Create stunning, high-performance mobile applications for iOS and Android that users love and that drive business results
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={400} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleContactClick}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#044D76',
                  '&:hover': { 
                    bgcolor: alpha('#044D76', 0.9),
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large" 
                href="#portfolio"
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { 
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                View Our Work
              </Button>
            </FadeInBox>
          </Box>
        </Container>

        {/* Wave shape divider */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 80.5C672 71 768 35.5 864 26.625C960 17.75 1056 35.5 1152 44.375C1248 53.25 1344 53.25 1392 53.25H1440V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
              fill={theme.palette.background.default}
            />
          </svg>
        </Box>
      </HeroBox>

      {/* Overview Section */}
      <Box component="section" ref={sectionRefs.overview} py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview}>
                <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
                  Exceptional Mobile Experiences for Modern Users
                </Typography>
                <Typography variant="body1" paragraph>
                  In today's mobile-first world, having a strong presence on iOS and Android is essential for business success. Our mobile development team creates intuitive, high-performance applications that delight users and drive engagement.
                </Typography>
                <Typography variant="body1" paragraph>
                  Whether you need a consumer-facing app or an enterprise solution, we leverage the latest mobile technologies and best practices to deliver outstanding results that exceed expectations.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                  {[
                    "Native & cross-platform solutions",
                    "Intuitive UI/UX design",
                    "Offline functionality",
                    "Seamless backend integration"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#044D76' }} />
                      <Typography variant="body1" fontWeight="medium">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </FadeInBox>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview} delay={300}>
                <Box sx={{ position: 'relative' }}>
                  {/* Improved code editor styling */}
                  <CodeEditor>
                    <EditorHeader>
                      <EditorDot color="#ff5f56" />
                      <EditorDot color="#ffbd2e" />
                      <EditorDot color="#27c93f" />
                      <EditorFilename>AppComponent.tsx</EditorFilename>
                    </EditorHeader>
                    
                    <CodeContent>
                      <PreformattedCode>
{`import React, { useEffect, useState } from 'react';

// Sample mobile app component structure
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Load user data when app starts
    async function loadUserData() {
      try {
        const response = await fetch('/api/user');
        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadUserData();
  }, []);

  if (isLoading) {
    return (
      <div className="loading-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <nav className="nav-bar">
          {/* Navigation items */}
        </nav>
      </header>
      
      <main className="main-content">
        {/* App screens would go here */}
        <HomeScreen user={userData} />
        <ProfileScreen user={userData} />
        <NotificationsScreen />
      </main>
      
      <footer className="app-footer">
        {/* Bottom navigation */}
      </footer>
    </div>
  );
}

// Example screen component
function HomeScreen({ user }) {
  return (
    <div className="screen">
      <h1>Welcome, {user?.name}</h1>
      {/* Screen content */}
    </div>
  );
}`}
                      </PreformattedCode>
                    </CodeContent>
                  </CodeEditor>
                  
                  {/* Background decoration */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -24, 
                      right: -24, 
                      width: 128, 
                      height: 128, 
                      borderRadius: '50%', 
                      bgcolor: '#0099ff',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -32, 
                      left: -32, 
                      width: 160, 
                      height: 160, 
                      borderRadius: '50%', 
                      bgcolor: '#044D76',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                </Box>
              </FadeInBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        component="section" 
        ref={sectionRefs.features}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.features}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our Mobile Development Services
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We offer comprehensive mobile development services to help businesses engage users and achieve their objectives across all major platforms.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {featureItems.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard 
                  elevation={2}
                  visible={isVisible.features}
                  delay={feature.delay}
                >
                  <IconBox className="featureIcon">
                    {feature.icon}
                  </IconBox>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    className="featureTitle"
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Box component="section" ref={sectionRefs.process} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.process}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our Mobile App Development Process
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We follow a structured approach to mobile app development that ensures your application meets your specific requirements and delivers an exceptional user experience.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <ProcessTimelineLine />
            
            {/* Process steps */}
            {processSteps.map((step, index) => (
              <ProcessStepBox 
                key={index}
                visible={isVisible.process}
                delay={step.delay}
                sx={{ 
                  flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                }}
              >
                <Box 
                  sx={{ 
                    width: '50%',
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    pr: index % 2 === 0 ? 6 : 0,
                    pl: index % 2 === 0 ? 0 : 6,
                  }}
                >
                  <Typography variant="h6" component="h3" gutterBottom sx={{ color: '#044D76' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                
                <ProcessIconBox>
                  {step.icon}
                </ProcessIconBox>
                
                <Box sx={{ width: '50%' }} />
              </ProcessStepBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Technologies Section - Improved */}
      <Box 
        component="section" 
        ref={sectionRefs.technologies}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.technologies}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Mobile Technologies We Use
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We leverage industry-leading mobile development frameworks and tools to build powerful, scalable, and user-friendly mobile applications.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {technologies.map((tech, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <TechCard 
                  elevation={1}
                  visible={isVisible.technologies}
                  delay={tech.delay}
                >
                  <TechIconBox className="techIcon">
                    {tech.icon}
                  </TechIconBox>
                  <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                    {tech.name}
                  </Typography>
                  <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                  <Typography variant="body2" color="text.secondary">
                    Expert implementation
                  </Typography>
                </TechCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box component="section" id="portfolio" py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our Mobile App Portfolio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore some of our successful mobile applications and the results we've delivered for our clients.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {portfolioProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard elevation={4}>
                  <Box sx={{ height: 240, position: 'relative' }}>
                    <ProjectImage 
                      src={project.image} 
                      alt={project.title}
                      className="projectImage"
                    />
                    <ProjectOverlay className="projectOverlay">
                      <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'common.white' }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ color: 'grey.200' }}>
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.tags.map((tag, i) => (
                          <Chip 
                            key={i}
                            label={tag}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(4, 77, 118, 0.8)', 
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    </ProjectOverlay>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                bgcolor: '#044D76',
                color: 'common.white',
                '&:hover': {
                  bgcolor: alpha('#044D76', 0.9),
                  transform: 'translateY(-4px)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box component="section" ref={sectionRefs.testimonials} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.testimonials}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Customer Testimonials
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover what our clients have to say about their experience working with us and the results we've delivered.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative', maxWidth: 'md', mx: 'auto' }}>
            {/* Left Navigation */}
            <TestimonialNavButton 
              sx={{ 
                left: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
            
            {/* Testimonial Card */}
            <TestimonialCard elevation={3}>
              <Typography 
                variant="body1" 
                component="blockquote" 
                sx={{ 
                  fontStyle: 'italic', 
                  position: 'relative',
                  mb: 4,
                  '&::before': {
                    content: '"\\201C"',
                    position: 'absolute',
                    left: -30,
                    top: -20,
                    fontSize: '5rem',
                    color: 'rgba(4, 77, 118, 0.1)',
                    fontFamily: 'Georgia, serif'
                  }
                }}
              >
                "The mobile app they developed for our business has transformed how we interact with customers. The team was professional, responsive, and delivered a product that exceeded our expectations in both functionality and design."
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Box 
                    key={index}
                    component="span"
                    sx={{ 
                      color: '#0099ff',
                      mx: 0.5,
                      fontSize: '24px',
                    }}
                  >
                    ★
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src="https://randomuser.me/api/portraits/women/32.jpg"
                  alt="Sarah Johnson"
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    mr: 2,
                    border: '2px solid rgba(4, 77, 118, 0.2)'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#044D76', fontWeight: 'bold' }}>
                    Sarah Johnson
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Director of Digital Products
                  </Typography>
                </Box>
              </Box>
            </TestimonialCard>
            
            {/* Right Navigation */}
            <TestimonialNavButton 
              sx={{ 
                right: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
          </Box>
        </Container>
      </Box>      
      <LetsTalk />
      <Footer />
    </Box>
  );
};

export default MobileDevelopmentPage;